import { t } from "i18next";
import { useEffect, useRef, useState } from "react";
import {
  abandonNoteSubmission,
  beginNoteDraft,
  completeNoteSubmission,
  NOTE_SUBMISSION_CHANGED,
  type NoteSubmission,
  prepareNoteSubmission,
  readNoteSubmission,
} from "@/lib/note-submission";
import {
  getVisitScope,
  refreshVisitReads,
  useVisitScope,
} from "@/lib/visit-queries";
import { createVisitNote } from "@/services/visits";

const inFlight = new Map<
  string,
  { account: string; parent: string; promise: Promise<{ public_id: string }> }
>();
const message = (failure: unknown): string =>
  failure instanceof Error
    ? failure.message
    : t("visit.recovery.noteUncertain");
export function useNoteGeneration({
  visitPublicId,
  open,
}: {
  visitPublicId: string | undefined;
  open: boolean;
}) {
  const scope = useVisitScope();
  const account = scope ? `${scope.uid}:${scope.accountPublicId}` : null;
  const active = useRef({ scope, visitPublicId, open });
  if (
    active.current.scope !== scope ||
    active.current.visitPublicId !== visitPublicId ||
    active.current.open !== open
  )
    active.current = { scope, visitPublicId, open };
  const lifetime = active.current;
  const mounted = useRef(true);
  const [, update] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const current = (): boolean =>
    mounted.current &&
    active.current === lifetime &&
    open &&
    !!scope &&
    getVisitScope() === scope;
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  useEffect(() => {
    setLoading(false);
    setError(null);
    if (lifetime.open && account && lifetime.visitPublicId) {
      try {
        beginNoteDraft(account, lifetime.visitPublicId);
        update((n) => n + 1);
      } catch (failure) {
        setError(message(failure));
      }
    }
  }, [lifetime, account]);
  let saved: NoteSubmission | null = null;
  let storageError: string | null = null;
  if (account && visitPublicId) {
    try {
      saved = readNoteSubmission(account, visitPublicId);
    } catch (failure) {
      storageError = message(failure);
    }
  }
  const pendingOperation = [...inFlight.values()].find(
    (entry) => entry.account === account && entry.parent === visitPublicId,
  );
  useEffect(() => {
    const changed = (): void => {
      update((n) => n + 1);
    };
    window.addEventListener(NOTE_SUBMISSION_CHANGED, changed);
    return () => {
      window.removeEventListener(NOTE_SUBMISSION_CHANGED, changed);
    };
  }, []);
  const beginDraft = (): boolean => {
    if (!current() || !account || !visitPublicId || pendingOperation)
      return false;
    try {
      if (readNoteSubmission(account, visitPublicId)?.kind === "pending")
        return false;
      beginNoteDraft(account, visitPublicId);
      setError(null);
      return true;
    } catch (failure) {
      setError(message(failure));
      return false;
    }
  };
  const run = async (body?: unknown): Promise<boolean> => {
    if (!current()) return false;
    setLoading(true);
    setError(null);
    try {
      if (!account || !visitPublicId || !scope)
        throw Error("Sign in and select a visit first.");
      const prepared = body
        ? prepareNoteSubmission(account, visitPublicId, body)
        : readNoteSubmission(account, visitPublicId);
      if (!prepared) throw Error("No saved note request to retry.");
      const key = `${account}:${scope.generation}:${visitPublicId}:${prepared.key}`;
      let operation = inFlight.get(key)?.promise;
      if (!operation) {
        operation = (
          prepared.kind === "accepted"
            ? Promise.resolve({ public_id: prepared.notePublicId })
            : createVisitNote(visitPublicId, prepared.body, {
                key: prepared.key,
                userUid: scope.uid,
              })
        ).finally(() => {
          inFlight.delete(key);
          window.dispatchEvent(new Event(NOTE_SUBMISSION_CHANGED));
        });
        inFlight.set(key, {
          account,
          parent: visitPublicId,
          promise: operation,
        });
      }
      const accepted = await operation;
      if (!current()) return false;
      completeNoteSubmission(
        account,
        visitPublicId,
        prepared,
        accepted.public_id,
      );
      void refreshVisitReads(scope);
      return true;
    } catch (failure) {
      if (current()) setError(message(failure));
      return false;
    } finally {
      if (current()) {
        setLoading(false);
        update((n) => n + 1);
      }
    }
  };
  const abandon = (): void => {
    if (!current() || !account || !visitPublicId || !scope) return;
    if (
      [...inFlight.values()].some(
        (entry) => entry.account === account && entry.parent === visitPublicId,
      )
    )
      return;
    try {
      abandonNoteSubmission(account, visitPublicId);
      setError(null);
      update((n) => n + 1);
    } catch (failure) {
      setError(message(failure));
    }
  };
  return {
    beginDraft,
    isCurrent: current,
    submit: (body: unknown) => run(body),
    retry: () => run(),
    abandon,
    recovery: saved?.kind === "pending" || !!storageError,
    loading: loading || !!pendingOperation,
    // This screen's own request is running (not one left by another screen).
    submitting: loading,
    error: error ?? storageError,
  };
}
